import React from 'react';

/**
 * ChanterSvg Component
 *
 * A reusable SVG component for rendering a Highland Bagpipe chanter.
 * Renders a detailed chanter with finger holes that respond to the active note.
 *
 * @component
 * @param {Object} props
 * @param {number} props.width - The width of the SVG container
 * @param {number} props.height - The height of the SVG container
 * @param {string} [props.activeNote] - Currently selected note (for standard fingering)
 * @param {Object} [props.fingeringState] - State of each finger hole (custom fingering)
 * @param {string} [props.melodyNote] - Melody note for grace note fingering
 * @param {string} [props.graceNote] - Grace note to show fingering for (requires melodyNote)
 * @param {boolean} [props.forceTaorluathGraceFingering] - Force grace note technique for taorluath middle note
 */
const ChanterSvg = ({ width, height, activeNote, fingeringState = {}, melodyNote, graceNote, forceTaorluathGraceFingering = false }) => {
  // Define hole states for front holes (7 holes)
  const frontHoleStates = {
    'High A': [false, false, true, true, true, true, false],
    'High G': [false, false, false, true, true, true, false],
    F: [true, false, false, true, true, true, false],
    E: [true, true, false, true, true, true, false],
    D: [true, true, true, false, false, false, true],
    C: [true, true, true, true, false, false, true],
    B: [true, true, true, true, true, false, false],
    'Low A': [true, true, true, true, true, true, false],
    'Low G': [true, true, true, true, true, true, true], // All covered
  };

  // Define back hole state (thumb hole)
  const backHoleStates = {
    'High A': false,
    'High G': true,
    F: true,
    E: true,
    D: true,
    C: true,
    B: true,
    'Low A': true,
    'Low G': true,
  };

  // Map note names to zero-based hole indices: 0 = thumb, 1-7 = front holes
  const noteToHoleIndex = {
    'High A': 0, // thumb
    'High G': 1,
    'F': 2,
    'E': 3,
    'D': 4,
    'C': 5,
    'B': 6,
    'Low A': 7,
    'Low G': 7
  };

  // Helper function to get grace note fingering
  const getGraceFingering = (melodyNote, graceNote, forceTaorluathGrace = false) => {
    // Define note order (lowest to highest)
    const noteOrder = [
      'Low G', 'Low A', 'B', 'C', 'D', 'E', 'F', 'High G', 'High A'
    ];
    
    const melodyIndex = noteOrder.indexOf(melodyNote);
    const graceIndex = noteOrder.indexOf(graceNote);
    
    // Use grace note technique (melody fingering minus grace hole) if:
    // 1. Grace note is HIGHER than melody note (traditional rule), OR
    // 2. This is forced for taorluath middle note
    if (graceIndex > melodyIndex || forceTaorluathGrace) {
      // Grace note is higher OR forced taorluath - use grace note technique
      const backHoleCovered = backHoleStates[melodyNote] !== undefined 
        ? backHoleStates[melodyNote] 
        : true;
        
      const frontHoles = frontHoleStates[melodyNote] || Array(7).fill(true);
      
      // Convert to the 8-element holes array format [thumb, front1, front2, ...]
      const holes = [backHoleCovered, ...frontHoles];
      
      // Uncover ONLY the grace note's hole (by index)
      const graceHoleIndex = noteToHoleIndex[graceNote];
      if (graceHoleIndex !== undefined) {
        holes[graceHoleIndex] = false;
      }
      
      return { holes };
    } else {
      // Grace note is at or below melody note - use standard fingering for grace note
      const backHoleCovered = backHoleStates[graceNote] !== undefined 
        ? backHoleStates[graceNote] 
        : true;
        
      const frontHoles = frontHoleStates[graceNote] || Array(7).fill(true);
      
      // Convert to the 8-element holes array format [thumb, front1, front2, ...]
      const holes = [backHoleCovered, ...frontHoles];
      
      return { holes };
    }
  };

  // Determine fingering based on parameters
  let thumbCovered, frontHoles;
  
  // Priority order: fingeringState.holes > grace note fingering > activeNote fingering
  if (Array.isArray(fingeringState.holes) && fingeringState.holes.length === 8) {
    // Custom fingering state provided
    thumbCovered = fingeringState.holes[0];
    frontHoles = fingeringState.holes.slice(1);
  } else if (melodyNote && graceNote) {
    // Grace note fingering requested
    const graceFingering = getGraceFingering(melodyNote, graceNote, forceTaorluathGraceFingering);
    thumbCovered = graceFingering.holes[0];
    frontHoles = graceFingering.holes.slice(1);
  } else {
    // Standard note fingering (backward compatible)
    frontHoles = frontHoleStates[activeNote] || Array(7).fill(true);
    thumbCovered = backHoleStates[activeNote] !== undefined ? backHoleStates[activeNote] : true;
  }

  // Scale factors for the SVG
  const scale = Math.min(width / 120, height / 450);
  const scaledWidth = 120 * scale;
  const scaledHeight = 450 * scale;
  const offsetX = (width - scaledWidth) / 2;
  const offsetY = (height - scaledHeight) / 2;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="chanter-svg">
      <g transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`}>
        {/* Floating shadow */}
        <ellipse cx="60" cy="440" rx="30" ry="5" fill="rgba(0, 0, 0, 0.1)" />

        {/* Chanter body */}
        <path d="M50 50 H70 L75 400 H45 Z" fill="#808080" stroke="#404040" strokeWidth="1" />

        {/* Slim mouthpiece */}
        <path d="M55 30 H65 L60 50 H60 Z" fill="#808080" stroke="#404040" strokeWidth="1" />

        {/* Back hole (thumb hole) */}
        <circle
          cx="40"
          cy="60"
          r="5"
          fill={thumbCovered ? '#000000' : '#FFFFFF'}
          stroke="#404040"
          strokeWidth="1"
        />

        {/* Front finger holes */}
        {frontHoles.map((isCovered, index) => (
          <circle
            key={index}
            cx="60"
            cy={80 + index * 40}
            r="5"
            fill={isCovered ? '#000000' : '#FFFFFF'}
            stroke="#404040"
            strokeWidth="1"
          />
        ))}
      </g>
    </svg>
  );
};

export default ChanterSvg;
